import type { CartItem } from "@ecommerce/store";
import { productQuantity, UNIT_MEASURE_CODE } from "@ecommerce/shared-utils";
import type {
    OrdenReanudacionProductoDTO,
    OrderCheckoutResumeDTO,
} from "../types/purchaseOrder/OrderResume";

const missingCanonicalStockReports = new Set<string>();

const reportMissingCanonicalStock = (
    producto: OrdenReanudacionProductoDTO,
    usedLegacyStock: boolean,
) => {
    const key = `${producto.idProducto}:${producto.idProductoVariante}`;
    if (missingCanonicalStockReports.has(key)) return;

    missingCanonicalStockReports.add(key);
    console.error("[cantidad] Falta stockAlmacen al reanudar una orden", {
        productId: producto.idProducto,
        variantId: producto.idProductoVariante,
        policy: usedLegacyStock ? "legacy-unit-stock" : "blocked-without-canonical-stock",
    });
};

const mapResumeProductToCartItem = (producto: OrdenReanudacionProductoDTO): CartItem => {
    const unitMeasureCode = producto.tinCodUnidadMedida ?? UNIT_MEASURE_CODE;
    const cantDecimal =
        unitMeasureCode === UNIT_MEASURE_CODE
            ? 0
            : productQuantity.resolvePrecision(producto.cantDecimal);
    const stockResolution = productQuantity.resolveStock({
        canonicalStock: producto.stockAlmacen,
        legacyStock: producto.stock,
        unitMeasureCode,
        // Reanudar una compra de tienda tampoco habilita sobreventa.
        allowWithoutStock: false,
    });

    if (stockResolution.missingCanonicalStock) {
        reportMissingCanonicalStock(producto, stockResolution.usedLegacyStock);
    }

    const stock =
        stockResolution.stock == null
            ? 0
            : (productQuantity.getMaximumSelectableStock(stockResolution.stock, cantDecimal) ?? 0);

    return {
        idProduct: producto.idProducto,
        idProductVariant: producto.idProductoVariante,
        name: producto.nombre,
        price: producto.precio,
        quantity: producto.cantidad,
        image: producto.urlImagenPrincipal,
        stock,
        unitMeasureCode,
        unitLabel:
            producto.unidadMedida?.trim() ||
            (unitMeasureCode === UNIT_MEASURE_CODE ? "Unidades" : "Medida"),
        cantDecimal,
        minimumQuantity: productQuantity.getMinimum({ cantDecimal }),
        attributes: (producto.atributos || []).map((atributo) => ({
            name: atributo.nombreAtributo,
            value: atributo.nombreAtributoValor,
        })),
    };
};

export const mapResumeProductsToCartItems = (resume: OrderCheckoutResumeDTO): CartItem[] =>
    (resume.Productos || []).map(mapResumeProductToCartItem);
